import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ProjectForm from '../components/ProjectForm';
import * as api from '../services/api';
import type { Progetto, ProgettoInput } from '../types';
import { useAuth } from '../context/AuthContext';

const ProgettiPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [showDisabled, setShowDisabled] = useState<'enabled' | 'all'>('enabled');
  const [progetti, setProgetti] = useState<Progetto[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingProgetto, setEditingProgetto] = useState<Progetto | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [editingCell, setEditingCell] = useState<{ progettoId: number; field: string } | null>(null);

  const loadProgetti = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.getProgetti(
        showDisabled === 'all',
        page + 1,
        rowsPerPage,
        searchTerm
      );
      setProgetti(response.data);
      setTotalCount(response.pagination.totalCount);
    } catch (error) {
      console.error('Error loading progetti:', error);
    } finally {
      setLoading(false);
    }
  }, [showDisabled, page, rowsPerPage, searchTerm]);

  useEffect(() => {
    loadProgetti();
  }, [loadProgetti]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(0); // Reset to first page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleAdd = () => {
    setEditingProgetto(null);
    setFormOpen(true);
  };

  const handleEdit = (progetto: Progetto) => {
    setEditingProgetto(progetto);
    setFormOpen(true);
  };

  const handleDelete = async (progetto: Progetto) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il progetto "${progetto.titolo}"?`)) {
      return;
    }

    try {
      await api.deleteProgetto(progetto.id);
      await loadProgetti();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Errore nell\'eliminazione del progetto');
    }
  };

  const handleSave = async (progettoData: ProgettoInput) => {
    if (editingProgetto) {
      await api.updateProgetto(editingProgetto.id, progettoData);
    } else {
      await api.createProgetto(progettoData);
    }
    await loadProgetti();
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleInlineEdit = async (progetto: Progetto, field: string, value: any) => {
    try {
      // Prepara i dati per l'update parziale
      const updateData: any = { ...progetto };

      if (field === 'budget_gg_uu') {
        updateData.budget_gg_uu = value ? parseFloat(value) : null;
      } else if (field === 'data_inizio' || field === 'data_fine') {
        updateData[field] = value || null;
      }

      await api.updateProgetto(progetto.id, updateData);
      await loadProgetti();
      setEditingCell(null);
    } catch (error: any) {
      console.error('Error updating field:', error);
      alert(error.response?.data?.error || 'Errore nell\'aggiornamento del campo');
    }
  };

  const renderEditableCell = (progetto: Progetto, field: string, displayValue: string) => {
    const isEditing = editingCell?.progettoId === progetto.id && editingCell?.field === field;
    const canUpdate = hasPermission('progetti', 'update');

    if (!canUpdate) {
      return <TableCell>{displayValue}</TableCell>;
    }

    if (isEditing) {
      if (field === 'budget_gg_uu') {
        return (
          <TableCell>
            <TextField
              type="number"
              size="small"
              defaultValue={progetto.budget_gg_uu || ''}
              autoFocus
              onBlur={(e) => handleInlineEdit(progetto, field, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleInlineEdit(progetto, field, (e.target as HTMLInputElement).value);
                } else if (e.key === 'Escape') {
                  setEditingCell(null);
                }
              }}
              sx={{ width: '100px' }}
            />
          </TableCell>
        );
      } else if (field === 'data_inizio' || field === 'data_fine') {
        const dateValue = progetto[field as keyof Progetto]
          ? new Date(progetto[field as keyof Progetto] as string).toISOString().split('T')[0]
          : '';
        return (
          <TableCell>
            <TextField
              type="date"
              size="small"
              defaultValue={dateValue}
              autoFocus
              onBlur={(e) => handleInlineEdit(progetto, field, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleInlineEdit(progetto, field, (e.target as HTMLInputElement).value);
                } else if (e.key === 'Escape') {
                  setEditingCell(null);
                }
              }}
              sx={{ width: '150px' }}
            />
          </TableCell>
        );
      }
    }

    return (
      <TableCell
        onClick={() => setEditingCell({ progettoId: progetto.id, field })}
        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
        title="Click per modificare"
      >
        {displayValue}
      </TableCell>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">
            Gestione Progetti
          </Typography>
          {hasPermission('progetti', 'create') && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
            >
              Nuovo Progetto
            </Button>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            size="small"
            placeholder="Cerca progetti..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />

          <ToggleButtonGroup
            value={showDisabled}
            exclusive
            onChange={(_, value) => {
              if (value) {
                setShowDisabled(value);
                setPage(0);
              }
            }}
            size="small"
          >
            <ToggleButton value="enabled">Solo Abilitati</ToggleButton>
            <ToggleButton value="all">Tutti</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TableContainer sx={{ position: 'relative', minHeight: 400 }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 1,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Titolo</TableCell>
                <TableCell>PM</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Data Inizio</TableCell>
                <TableCell>Data Fine</TableCell>
                <TableCell>Budget (gg)</TableCell>
                <TableCell>Budget Residuo (gg)</TableCell>
                <TableCell>Tot. Allocato (gg)</TableCell>
                <TableCell>Tot. Rendicontato (gg)</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {progetti.map((progetto) => (
                <TableRow key={progetto.id} hover>
                  <TableCell>{progetto.titolo}</TableCell>
                  <TableCell>{progetto.pm || '-'}</TableCell>
                  <TableCell>{progetto.cliente || '-'}</TableCell>
                  {renderEditableCell(
                    progetto,
                    'data_inizio',
                    progetto.data_inizio
                      ? new Date(progetto.data_inizio).toLocaleDateString('it-IT')
                      : '-'
                  )}
                  {renderEditableCell(
                    progetto,
                    'data_fine',
                    progetto.data_fine
                      ? new Date(progetto.data_fine).toLocaleDateString('it-IT')
                      : '-'
                  )}
                  {renderEditableCell(
                    progetto,
                    'budget_gg_uu',
                    progetto.budget_gg_uu?.toString() || '-'
                  )}
                  <TableCell>{progetto.budget_residuo_gg_uu || '-'}</TableCell>
                  <TableCell>{progetto.totale_allocato != null ? progetto.totale_allocato.toFixed(1) : '-'}</TableCell>
                  <TableCell>{progetto.totale_rendicontato != null ? progetto.totale_rendicontato.toFixed(1) : '-'}</TableCell>
                  <TableCell>
                    {progetto.enabled ? (
                      <Chip icon={<CheckCircleIcon />} label="Attivo" color="success" size="small" />
                    ) : (
                      <Chip icon={<CancelIcon />} label="Disabilitato" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {hasPermission('progetti', 'update') && (
                      <IconButton size="small" onClick={() => handleEdit(progetto)} color="primary">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {hasPermission('progetti', 'delete') && (
                      <IconButton size="small" onClick={() => handleDelete(progetto)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && progetti.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Nessun progetto trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} di ${count !== -1 ? count : `più di ${to}`}`
          }
        />
      </Paper>

      <ProjectForm
        open={formOpen}
        progetto={editingProgetto}
        onClose={() => {
          setFormOpen(false);
          setEditingProgetto(null);
        }}
        onSave={handleSave}
      />
    </Container>
  );
};

export default ProgettiPage;
